import {
    useFocusEffect,
    useNavigation
} from '@react-navigation/native';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import { Platform } from 'react-native';
import { useSelector } from 'react-redux';

import {
    BigButton,
    Page,
    Selector,
    showNotification,
    Title,
    View
} from '../../../common';
import { QAMode } from '../../../common/Log';
import { useBackup } from '../../../hooks/useBackup';
import { getFormValues } from '../../../selectors/forms';
import {
    backupInfoProcessing,
    toLog
} from './Backup';
import { BackupList } from './BackupList';
import {
    getGoogleDriveBackupInfo,
    restoreGoogleBackup
} from './google/googleDrive';
import {
    getICloudBackupInfo,
    restoreICloud
} from './icloud/icloud';
import { styles } from './styles';

const CLOUD_TYPES = {
    ICLOUD: 'iCloud',
    GOOGLE: 'google',
};

const cloudData = [
    {
        id: CLOUD_TYPES.ICLOUD,
        name: 'iCloud',
    },
    {
        id: CLOUD_TYPES.GOOGLE,
        name: 'Google Drive',
    },
];
const ChooseCloud = ({
    onChangeCloud,
    iCloud,
    google,
}) => {
    const [ cData ] = useState(cloudData?.filter?.((c) => {
        if (c.id === CLOUD_TYPES.ICLOUD) {
            return Platform.OS === 'ios';
        }
        return true;
    }));
    const [ selectedCloud, setSelectedCloud ] = useState(
        iCloud && Platform.OS === 'ios' ? CLOUD_TYPES.ICLOUD : google ? CLOUD_TYPES.GOOGLE : undefined,
    );

    const availableCloudCount = useMemo(() => (iCloud && Platform.OS === 'ios' ? 1 : 0) + (google ? 1 : 0),
        [ iCloud, google ]);

    useEffect(() => {
        onChangeCloud?.(selectedCloud);
    },
    [ selectedCloud, onChangeCloud ]);

    const handleCloudSelection = useCallback((cloud) => {
        setSelectedCloud(cloud?.id);
    },
    []);

    if (availableCloudCount <= 1) {
        return null;
    }

    return (
        <View style={[ styles.container, styles.selectorContainer ]}>
            <Selector
                data={cData}
                selected={selectedCloud}
                onSelect={handleCloudSelection}/>
        </View>
    );
};

export const Restore = Page.register(() => {
    const navigation = useNavigation();
    const {
        googleCloudEnabled,
        iCloudEnabled,
        profileId,
    } = useBackup({ useInteractive: false });
    const [ selectedCloud, setSelectedCloud ] = useState();
    const [ isRestoring, setIsRestoring ] = useState(false);
    const { backupProcessing = false } = useSelector(getFormValues);

    const handleCancel = useCallback(() => navigation.goBack(),
        [ navigation ]);

    const handleRestore = useCallback(() => {
        if (!selectedCloud) {
            showNotification('errors.noCloudSelected', false, true);
            return;
        }
        backupInfoProcessing.disableInteractive = false;
        setIsRestoring(true);

        if (CLOUD_TYPES.GOOGLE === selectedCloud) {
            getGoogleDriveBackupInfo(profileId, true)
                .then((info) => {
                    toLog('Restore:Google backup info', info);
                    const count = info?.backups?.length || 0;
                    if (count === 1) {
                        restoreGoogleBackup(info?.backups[0], true)
                            .then(() => {
                                showNotification('notifications.restoreCompleted');
                                navigation.goBack();
                            })
                            .catch((e) => {

                                console.log('Restore error:1', e);
                                showNotification(Object.R('errors.restoreFailed'), false, true);
                            })
                            .finally(() => setIsRestoring(false));
                    } else if (count > 1) {
                        Page.showModal(<BackupList
                            list={info?.backups}
                            attachSize={info?.attachSize}
                            isIos={false}
                            onCancel={() => {
                                setIsRestoring(false);
                            }}
                            onSelect={(i) => restoreGoogleBackup(i, true)
                                .then(() => {
                                    showNotification(Object.R('notifications.restoreCompleted'));
                                    navigation.goBack();
                                })
                                .catch((e) => {
                                    toLog('Restore:Google backup error:1', e);
                                    showNotification(Object.R('errors.restoreFailed'), false, true);
                                })
                                .finally(() => setIsRestoring(false))}/>);
                    } else {
                        toLog('No backups found for Google Drive - restore', info);
                        showNotification(Object.R('errors.googleDriveRestoreNoData'), false, true);
                    }
                })
                .catch((e) => {
                    toLog('Restore:Google backup error:2', e);
                    showNotification(Object.R('errors.restoreFailed'), false, true);
                })
                .finally(() => setIsRestoring(false));
        } else {
            getICloudBackupInfo(profileId)
                .then((info) => {
                    restoreICloud({
                        info,
                        onComplete: () => {
                            showNotification(Object.R('notifications.restoreCompleted'));
                            navigation.goBack();
                        },
                        onCancel: () => {
                            setIsRestoring(false);
                        },
                        onError: () => {
                            setIsRestoring(false);
                            showNotification(Object.R('errors.restoreFailed'), false, true);
                        },
                    });


                })
                .catch((e) => {

                    console.log('Restore error:3', e);
                    showNotification(Object.R('errors.restoreFailed'), false, true);
                });
            // .finally(() => setIsRestoring(false));
        }

    },
    [ selectedCloud,
        navigation,
        profileId ]);

    const handleCloudChange = useCallback((cloud) => setSelectedCloud(cloud),
        []);

    useFocusEffect(useCallback(() => {
        if (!googleCloudEnabled && !iCloudEnabled) {
            // showNotification('errors.noCloudEnabled');
            navigation.goBack();
        }
    }, []));

    return (
        <Page
            safeArea
            isLoading={isRestoring || backupProcessing}
            footer={
                <View style={styles.buttonsContainer}>
                    <BigButton
                        styles={styles.cancelButton}
                        titleStyles={styles.cancelButtonTitle}
                        title={'buttons.cancel'}
                        action={handleCancel}
                        disabled={isRestoring || backupProcessing}
                        disabledStyle={styles.disabled}
                    />
                    <BigButton
                        styles={styles.saveButton}
                        titleStyles={styles.saveButtonTitle}
                        title={'buttons.execute'}
                        action={handleRestore}
                        disabled={!selectedCloud || backupProcessing || isRestoring}
                        disabledStyle={styles.disabled}
                    />
                </View>
            }
        >
            <View style={styles.container}>
                <View style={[ styles.titleContainer, styles.listItemText ]}>
                    <Title numberOfLines={0}>{Object.R('titles.restoreAfterLogin')}</Title>
                </View>
                <ChooseCloud
                    onChangeCloud={handleCloudChange}
                    google={googleCloudEnabled}
                    iCloud={iCloudEnabled}
                />
                <QAMode>
                    <Title>Google: {googleCloudEnabled ? '+' : '-'}</Title>
                    <Title>iCloud: {iCloudEnabled ? '+' : '-'}</Title>
                    <Title>Selected: {selectedCloud}</Title>
                </QAMode>
            </View>
        </Page>
    );
});
